"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface CreateProjectInlineProps {
  onSubmit: (domain: string) => void | Promise<void>;
  isSubmitting?: boolean;
  label?: string;
  className?: string;
}

/**
 * Domain-only project creation, inline in whatever list hosts it.
 *
 * Presentational on purpose: the network call lives with the caller so
 * the same row works on the workspaces card and in the project
 * switcher. It also means this never unmounts its own parent — the
 * failure mode that made the old modal render a blank page (#1113).
 */
export function CreateProjectInline({
  onSubmit,
  isSubmitting = false,
  label = "New project",
  className,
}: CreateProjectInlineProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [domain, setDomain] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  // A hardcoded id would collide when two hosts render this
  // component at once (e.g. /workspaces renders the sidebar
  // ProjectSwitcher alongside the org cards) — both <label htmlFor>
  // would then resolve to whichever input mounted first. useId()
  // keeps id/htmlFor paired per-instance.
  const domainInputId = useId();

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    setDomain("");
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={cn(
          "mt-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5",
          "text-sm text-muted-foreground hover:bg-accent",
          className,
        )}
      >
        <span
          className={cn(
            "flex size-6 items-center justify-center rounded-md border",
          )}
        >
          <Plus className="size-3.5" />
        </span>
        {label}
      </button>
    );
  }

  return (
    <form
      aria-label={label}
      className={cn("mt-2 space-y-2 rounded-md border p-2", className)}
      onSubmit={(e) => {
        e.preventDefault();
        const value = domain.trim();
        if (!value) return;
        void onSubmit(value);
      }}
      onBlur={(e) => {
        // `relatedTarget` is null both when focus moves outside the form
        // AND when it leaves the document entirely (tab switch, devtools,
        // browser chrome). Only the first is a dismissal — collapsing on
        // the second wipes the domain the user was midway through typing.
        if (!document.hasFocus()) return;
        const next = (e.relatedTarget ?? document.activeElement) as Node | null;
        if (next && e.currentTarget.contains(next)) return;
        close();
      }}
    >
      <Label htmlFor={domainInputId} className="text-xs">
        Website{" "}
        <span className="text-muted-foreground">
          (so we can analyze your brand)
        </span>
      </Label>
      <div className="flex gap-2">
        <Input
          id={domainInputId}
          ref={inputRef}
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") close();
          }}
          placeholder="https://yourcompany.com"
          disabled={isSubmitting}
          required
          className="h-8 text-sm"
        />
        <Button type="submit" size="sm" loading={isSubmitting}>
          Add
        </Button>
      </div>
    </form>
  );
}
